/** One person's figures on the Opening balances screen, as typed (plain digits). */
export type OpeningEdit = { wagesDue: string; advance: string; recoveryPerPay: string };

export type OpeningPerson = {
  id: string;
  code: string;
  name: string;
  pendingConfirmation?: boolean;
  opening?: { date: string; wagesDue: string; advance: string; recoveryPerPay?: string | null } | null;
  wagesDue?: string;
  advanceOutstanding?: string;
};

const num = (v?: string | null) => Number(v || 0);

/** What the form starts with: the opening in force, else empty. */
export const startEdit = (p: OpeningPerson): OpeningEdit => ({
  wagesDue: p.opening && num(p.opening.wagesDue) ? String(num(p.opening.wagesDue)) : "",
  advance: p.opening && num(p.opening.advance) ? String(num(p.opening.advance)) : "",
  recoveryPerPay: p.opening?.recoveryPerPay ? String(num(p.opening.recoveryPerPay)) : "",
});

/**
 * Rows to send: people whose figures or date differ from the opening in force. Someone without an opening is sent only
 * when a figure was typed (a typed 0 counts: "owes nothing on that day" still starts the account there).
 */
export const changedRows = (people: OpeningPerson[], edits: Record<string, OpeningEdit>, date: string) =>
  people
    .filter((p) => {
      const e = edits[p.id];
      if (!e) return false;
      const typed = e.wagesDue.trim() !== "" || e.advance.trim() !== "";
      if (!p.opening) return typed;
      return (
        p.opening.date !== date ||
        num(p.opening.wagesDue) !== num(e.wagesDue) ||
        num(p.opening.advance) !== num(e.advance) ||
        (num(e.advance) > 0 && num(p.opening.recoveryPerPay) !== num(e.recoveryPerPay))
      );
    })
    .map((p) => {
      const e = edits[p.id];
      return {
        personId: p.id,
        wagesDue: e.wagesDue.trim() || "0",
        advance: e.advance.trim() || "0",
        recoveryPerPay: num(e.advance) > 0 ? e.recoveryPerPay.trim() || undefined : undefined,
      };
    });

/** A row that can't be saved yet: an advance without its cut per payout. */
export const missingCut = (e?: OpeningEdit) => !!e && num(e.advance) > 0 && !(num(e.recoveryPerPay) > 0);
